/**
 * Utility-Funktionen für Tailwind CSS
 *
 * Diese Datei enthält Helper-Funktionen für:
 * - className merging
 * - Conditional classes
 * - Responsive utilities
 */

import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";

/**
 * Utility-Funktion für className merging
 * Kombiniert clsx und tailwind-merge für optimale Performance
 *
 * @param inputs - ClassValue Array für className merging
 * @returns Merged className string
 *
 * @example
 * cn('px-4 py-2', 'bg-blue-500', { 'text-white': isActive })
 * cn('px-4', 'px-6') // 'px-6' (letzter gewinnt)
 */
export function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs));
}

/**
 * Conditional className helper
 *
 * @param condition - Boolean condition
 * @param trueClass - Class wenn condition true
 * @param falseClass - Class wenn condition false (optional)
 * @returns Conditional className
 *
 * @example
 * conditional('px-4', 'px-6', isActive)
 * conditional('text-red-500', undefined, hasError)
 */
export function conditional(
  trueClass: string,
  falseClass?: string,
  condition?: boolean
): string {
  if (condition === undefined) return "";
  return condition ? trueClass : falseClass || "";
}

/**
 * Responsive className helper
 *
 * @param base - Base classes (mobile)
 * @param md - Medium screen classes
 * @param lg - Large screen classes
 * @param xl - Extra large screen classes
 * @returns Responsive className string
 *
 * @example
 * responsive('text-sm', 'text-base', 'text-lg', 'text-xl')
 */
export function responsive(
  base: string,
  md?: string,
  lg?: string,
  xl?: string
): string {
  const classes = [base];
  if (md) classes.push(`md:${md}`);
  if (lg) classes.push(`lg:${lg}`);
  if (xl) classes.push(`xl:${xl}`);
  return classes.join(" ");
}

/**
 * Variant className helper
 *
 * @param variants - Object mit Varianten-Mapping
 * @param variant - Aktuelle Variante
 * @param defaultVariant - Fallback-Variante
 * @returns Variant className
 *
 * @example
 * variant({
 *   primary: 'bg-blue-500 text-white',
 *   secondary: 'bg-gray-500 text-white'
 * }, 'primary')
 */
export function variant<T extends string>(
  variants: Record<T, string>,
  variant: T,
  defaultVariant?: T
): string {
  return variants[variant] || variants[defaultVariant as T] || "";
}

/**
 * Size className helper
 *
 * @param sizes - Object mit Size-Mapping
 * @param size - Aktuelle Size
 * @param defaultSize - Fallback-Size
 * @returns Size className
 *
 * @example
 * size({
 *   sm: 'px-2 py-1 text-sm',
 *   md: 'px-4 py-2 text-base',
 *   lg: 'px-6 py-3 text-lg'
 * }, 'md')
 */
export function size<T extends string>(
  sizes: Record<T, string>,
  size: T,
  defaultSize?: T
): string {
  return sizes[size] || sizes[defaultSize as T] || "";
}

/**
 * Animation className helper
 *
 * @param animation - Animation type
 * @param duration - Animation duration
 * @param delay - Animation delay
 * @returns Animation className
 *
 * @example
 * animation('fade-in', '300ms', '100ms')
 */
export function animation(
  animation: string,
  duration?: string,
  delay?: string
): string {
  const classes = [`animate-${animation}`];
  if (duration) classes.push(`duration-[${duration}]`);
  if (delay) classes.push(`delay-[${delay}]`);
  return classes.join(" ");
}

/**
 * Spacing className helper
 *
 * @param direction - Spacing direction (p, m, px, py, mx, my, etc.)
 * @param value - Spacing value (number oder string)
 * @returns Spacing className
 *
 * @example
 * spacing('p', 4) // 'p-4'
 * spacing('mx', 'auto') // 'mx-auto'
 */
export function spacing(direction: string, value: number | string): string {
  return `${direction}-${value}`;
}

/**
 * Color className helper
 *
 * @param property - CSS property (bg, text, border, etc.)
 * @param color - Color name
 * @param opacity - Color opacity (optional)
 * @returns Color className
 *
 * @example
 * color('bg', 'blue-500') // 'bg-blue-500'
 * color('text', 'gray-600', 80) // 'text-gray-600/80'
 */
export function color(
  property: string,
  color: string,
  opacity?: number
): string {
  const opacitySuffix = opacity ? `/${opacity}` : "";
  return `${property}-${color}${opacitySuffix}`;
}

/**
 * Typography className helper
 *
 * @param size - Font size
 * @param weight - Font weight
 * @param color - Text color
 * @returns Typography className
 *
 * @example
 * typography('lg', 'bold', 'gray-900')
 */
export function typography(
  size?: string,
  weight?: string,
  color?: string
): string {
  const classes: string[] = [];
  if (size) classes.push(`text-${size}`);
  if (weight) classes.push(`font-${weight}`);
  if (color) classes.push(`text-${color}`);
  return classes.join(" ");
}

/**
 * Layout className helper
 *
 * @param display - Display type
 * @param direction - Flex direction
 * @param justify - Justify content
 * @param align - Align items
 * @returns Layout className
 *
 * @example
 * layout('flex', 'col', 'center', 'center')
 */
export function layout(
  display?: string,
  direction?: string,
  justify?: string,
  align?: string
): string {
  const classes: string[] = [];
  if (display) classes.push(display);
  if (direction) classes.push(`flex-${direction}`);
  if (justify) classes.push(`justify-${justify}`);
  if (align) classes.push(`items-${align}`);
  return classes.join(" ");
}

/**
 * Border className helper
 *
 * @param width - Border width
 * @param style - Border style
 * @param color - Border color
 * @param radius - Border radius
 * @returns Border className
 *
 * @example
 * border(1, 'solid', 'gray-300', 'lg')
 */
export function border(
  width?: number,
  style?: string,
  color?: string,
  radius?: string
): string {
  const classes: string[] = [];
  if (width) classes.push(`border-${width}`);
  if (style) classes.push(`border-${style}`);
  if (color) classes.push(`border-${color}`);
  if (radius) classes.push(`rounded-${radius}`);
  return classes.join(" ");
}

/**
 * Shadow className helper
 *
 * @param size - Shadow size
 * @param color - Shadow color
 * @returns Shadow className
 *
 * @example
 * shadow('lg', 'gray-900')
 */
export function shadow(size?: string, color?: string): string {
  const classes: string[] = [];
  if (size) classes.push(`shadow-${size}`);
  if (color) classes.push(`shadow-${color}`);
  return classes.join(" ");
}

/**
 * Transition className helper
 *
 * @param property - Transition property
 * @param duration - Transition duration
 * @param timing - Transition timing function
 * @returns Transition className
 *
 * @example
 * transition('all', '300', 'ease-out')
 */
export function transition(
  property?: string,
  duration?: string,
  timing?: string
): string {
  const classes: string[] = [];
  if (property) classes.push(`transition-${property}`);
  if (duration) classes.push(`duration-${duration}`);
  if (timing) classes.push(`ease-${timing}`);
  return classes.join(" ");
}

// Export alle Utility-Funktionen
export { clsx, twMerge, type ClassValue };
